import { Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCookie } from "typescript-cookie";
import { ISelectOption } from "../../../models/utils/selectOption";
import { useGetDataOnScroll } from "../../getDataOnScrollProps/useGetDataOnScrollProps";
import { defaultFilterOption } from "../../../utils";
import { EntInitialFilter } from "../../../models/filters/initialFilter";

interface GeneralSelectProps<TEntity, TFilter extends EntInitialFilter> {
  queryHook: () => ReturnType<() => any>;
  initialFilter: TFilter;
  onChange?: (value: number) => void;
  onChangeMultiple?: (value: number[]) => void;
  value?: number|undefined|null;
  valueMultiple?: number[];
  mode?: "multiple"|"tags"|undefined;
  status?: any;
}

const GeneralSelect = <TEntity, TFilter extends EntInitialFilter>({
																	queryHook,
																	initialFilter,
																	onChange,
																	onChangeMultiple,
																	value,
																	valueMultiple,
																	mode,
																	status
																  }: GeneralSelectProps<TEntity, TFilter>) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ISelectOption[]>([]);

  const [getData] = queryHook();
  const {
	fetching,
	data,
	setData,
	setFetching,
	setFilter,
	scrollHandler,
	setCurrentPage,
  } = useGetDataOnScroll<TEntity, TFilter>({
	initialFilter,
	query: getData,
  });

  useEffect(() => {
	const transformedOptions = data.map((item: any) => ({
	  label: getCookie("i18next") === "ky" ? item.nameKy : item.nameRu,
	  value: item.id ?? "",
	}));
	setOptions(transformedOptions);
  }, [data]);

  const search = (searchValue?: string) => {
	if (!searchValue) return;
	const filter = { ...initialFilter, name: searchValue } as TFilter;
	setFilter(filter);
	setData([]);
	setCurrentPage(0);
	setFetching(true);
  };

  const clear = () => {
	const filter = { ...initialFilter, name: null } as TFilter;
	setFilter(filter);
	setData([]);
	setCurrentPage(0);
	setFetching(true);
  };

  return (
	<Select
	  mode={ mode }
	  status={ status }
	  placeholder={ t("msg.selectPlaceholder") }
	  onClear={ clear }
	  onSearch={ search }
	  onPopupScroll={ scrollHandler }
	  loading={ fetching }
	  value={
		mode === "multiple"
		  ? valueMultiple ?? []
		  : value ?? null
	  }
	  onChange={(val) => {
		if (mode === "multiple") {
		  onChangeMultiple?.(val as number[]);
		} else {
		  onChange?.(val as number);
		}
	  }}
	  options={ options }
	  filterOption={ defaultFilterOption }
	  showSearch
	  allowClear
	  dropdownRender={ (menu) => <Spin spinning={ fetching }>{ menu }</Spin> }
	/>
  );
};

export default GeneralSelect;
